// Entity-State pure functions. 1:1 portiert aus V1 useEntityState.js.
// - VALID_TRANSITIONS: erlaubte State-Übergänge
// - canTransition: Validierungs-Check
// - emotionalForState: Default-EmotionalContext für einen State
// - calculateConfidence: 0-100 score aus Autonomy/Stress/Focus/CognitiveLoad
// - calculateHealth: 4 Score-Werte (overall/network/cognitive/performance)
// - applyMarketEvent: Pure handler, gibt next state + Delta-Werte zurück

import type {
  EmotionalContext,
  EntityState,
  EntityStatusSummary,
  MarketEvent,
} from '@/types/eve-state';

export const TOTAL_NODES = 22;
export const DEFAULT_AUTONOMY = 65;

/**
 * State-Machine: erlaubte Übergänge pro Source-State. V1-Konvention 1:1.
 */
export const VALID_TRANSITIONS: Readonly<Record<EntityState, ReadonlyArray<EntityState>>> = {
  IDLE: ['MONITORING', 'ANALYZING'],
  MONITORING: ['ANALYZING', 'DECIDING', 'EXECUTING'],
  ANALYZING: ['DECIDING', 'MONITORING', 'STRESSED'],
  DECIDING: ['EXECUTING', 'ANALYZING'],
  EXECUTING: ['MONITORING', 'ANALYZING'],
  STRESSED: ['MONITORING'],
  FOCUSED: ['EXECUTING', 'ANALYZING'],
  CONFIDENT: ['EXECUTING', 'DECIDING'],
};

export function canTransition(from: EntityState, to: EntityState): boolean {
  return VALID_TRANSITIONS[from].includes(to);
}

/**
 * Liefert default EmotionalContext für einen State (V1: nach State-Setzung wird
 * die Emotional Context entsprechend angepasst).
 */
export function emotionalForState(state: EntityState): EmotionalContext | null {
  switch (state) {
    case 'STRESSED':
      return 'STRESSED';
    case 'EXECUTING':
      return 'CONFIDENT';
    case 'ANALYZING':
      return 'FOCUSED';
    default:
      return null;
  }
}

export interface ConfidenceInputs {
  autonomyLevel: number;
  stressLevel: number;
  focusLevel: number;
  cognitiveLoad: number;
}

/**
 * V1-Formel:
 * base=70, +(autonomy/100)*15, -(stress/100)*20, +(focus/100)*10, -(cognitive/100)*10
 * Geclampt auf 0..100, gerundet.
 */
export function calculateConfidence(inputs: ConfidenceInputs): number {
  const baseConfidence = 70;
  const autonomyBonus = (inputs.autonomyLevel / 100) * 15;
  const stressPenalty = (inputs.stressLevel / 100) * 20;
  const focusBonus = (inputs.focusLevel / 100) * 10;
  const cognitivePenalty = (inputs.cognitiveLoad / 100) * 10;
  const confidence = baseConfidence + autonomyBonus - stressPenalty + focusBonus - cognitivePenalty;
  return Math.max(0, Math.min(100, Math.round(confidence)));
}

export interface HealthInputs {
  activeNodes: number;
  totalNodes: number;
  stressLevel: number;
  focusLevel: number;
  cognitiveLoad: number;
  decisionConfidence: number;
}

/**
 * V1 `getStatusSummary().health` — 4 Score-Werte.
 */
export function calculateHealth(inputs: HealthInputs): EntityStatusSummary['health'] {
  const nodeRatio = inputs.activeNodes / inputs.totalNodes;
  return {
    overall: Math.round(nodeRatio * (100 - inputs.stressLevel / 2) * (inputs.focusLevel / 100)),
    network: Math.round(nodeRatio * 100),
    cognitive: Math.round(((100 - inputs.cognitiveLoad) * (100 - inputs.stressLevel)) / 100),
    performance: Math.round((inputs.focusLevel * inputs.decisionConfidence) / 100),
  };
}

export interface MarketEventEffect {
  /** Ziel-State der Transition (oder `null` wenn keine Transition triggered). */
  targetState: EntityState | null;
  /** Delta auf stressLevel (additiv). */
  stressDelta: number;
  /** Delta auf cognitiveLoad (additiv). */
  cognitiveDelta: number;
  /** Delta auf activeNodes (additiv, kann negativ sein). */
  nodesDelta: number;
  /** Override für EmotionalContext (überschreibt emotionalForState). */
  emotionalOverride: EmotionalContext | null;
  /** Wenn true: activeNodes auf totalNodes setzen (RECOVERY). */
  resetActiveNodes: boolean;
}

/**
 * Pure handler — gibt zurück was bei einem MarketEvent passiert. V1-Verhalten:
 * - HIGH_VOLATILITY → state ANALYZING, +15 stress, +20 cognitive
 * - CLEAR_SIGNAL    → state DECIDING, emotional CONFIDENT
 * - SYSTEM_ALERT    → state STRESSED, -3 nodes
 * - RECOVERY        → state MONITORING, activeNodes = total, -10 stress
 */
export function applyMarketEvent(event: MarketEvent): MarketEventEffect {
  const empty: MarketEventEffect = {
    targetState: null,
    stressDelta: 0,
    cognitiveDelta: 0,
    nodesDelta: 0,
    emotionalOverride: null,
    resetActiveNodes: false,
  };
  switch (event.type) {
    case 'HIGH_VOLATILITY':
      return { ...empty, targetState: 'ANALYZING', stressDelta: 15, cognitiveDelta: 20 };
    case 'CLEAR_SIGNAL':
      return { ...empty, targetState: 'DECIDING', emotionalOverride: 'CONFIDENT' };
    case 'SYSTEM_ALERT':
      return { ...empty, targetState: 'STRESSED', nodesDelta: -3 };
    case 'RECOVERY':
      return { ...empty, targetState: 'MONITORING', stressDelta: -10, resetActiveNodes: true };
    default:
      return empty;
  }
}

/**
 * V1 `adjustAutonomy` setzt nach Clamp ggf. EmotionalContext:
 * - autonomy > 80 → CONFIDENT
 * - autonomy < 30 → CAUTIOUS
 * - sonst: keine Änderung
 */
export function emotionalForAutonomy(autonomy: number): EmotionalContext | null {
  if (autonomy > 80) return 'CONFIDENT';
  if (autonomy < 30) return 'CAUTIOUS';
  return null;
}

/** Clampt 0..100. */
export function clamp01(v: number): number {
  return Math.max(0, Math.min(100, v));
}
